import type { Message } from '@/features/messages';
import dayjs from 'dayjs';

/**
 * Remove duplicated messages by messageId. Keeps the latest occurrence
 * (last in the input array) for each non-empty messageId. Messages without
 * a messageId are preserved as-is and are not deduplicated.
 */
function dedupeByMessageId(messages: Message[]): Message[] {
  const latestById = new Map<string, Message>();
  const messagesWithoutId: Message[] = [];

  for (const message of messages) {
    const id = message.messageId;
    if (id && id.length > 0) {
      latestById.set(id, message);
    } else {
      messagesWithoutId.push(message);
    }
  }

  return [...messagesWithoutId, ...latestById.values()];
}

/**
 * Xác định xem có nên hiển thị marker ngày trước tin nhắn hiện tại hay không.
 * Marker ngày được hiển thị nếu:
 * - Là tin nhắn đầu tiên trong danh sách, HOẶC
 * - Khoảng cách thời gian giữa tin nhắn hiện tại và tin nhắn trước đó từ 15 phút trở lên.
 *
 * @param messages - Danh sách tin nhắn đang được xử lý
 * @param index - Chỉ số của tin nhắn hiện tại trong danh sách
 * @returns boolean - true nếu nên hiển thị marker ngày trước tin nhắn này
 */
function shouldShowDateMarker(messages: Message[], index: number): boolean {
  if (index === 0) return true;

  const currentMessage = messages[index];
  const previousMessage = messages[index - 1];

  const timeDiff = dayjs(currentMessage.sentAt).valueOf() - dayjs(previousMessage.sentAt).valueOf();
  const fifteenMinutesInMs = 15 * 60 * 1000;

  return timeDiff >= fifteenMinutesInMs;
}

/**
 * Xử lý danh sách tin nhắn để chèn marker ngày và nhóm tin nhắn.
 * - Sắp xếp tin nhắn theo thời gian gửi tăng dần.
 * - Chèn marker ngày khi khoảng cách thời gian với tin nhắn trước ≥ 15 phút.
 * - Nhóm các tin nhắn liên tiếp của cùng người gửi trong cùng ngày.
 * - Ẩn avatar và tên người gửi trong các tin nhắn trung gian để tạo nhóm trực quan.
 *
 * @param messages - Danh sách tin nhắn cần xử lý
 * @returns Message[] - Danh sách tin nhắn đã được xử lý, bao gồm cả marker ngày
 */
export function insertSeparate(messages: Message[]): Message[] {
  if (messages.length === 0) return [];

  // Remove duplicated messages by messageId before grouping/marking
  const uniqueMessages = dedupeByMessageId(messages);

  const sorted = [...uniqueMessages].sort((a, b) => a.sentAt.valueOf() - b.sentAt.valueOf());

  const result: Message[] = [];
  for (let i = 0; i < sorted.length; i++) {
    const msg = sorted[i];
    let hideAvatar = true;
    let hideName = true;
    const msgSenderId = msg.senderId || null;

    const showDateMarker = shouldShowDateMarker(sorted, i);
    const msgDate = dayjs(msg.sentAt).format('YYYY-MM-DD');

    let isGroupEnd = true;
    if (i < sorted.length - 1) {
      const nextMsg = sorted[i + 1];
      const nextDate = dayjs(nextMsg.sentAt).format('YYYY-MM-DD');
      const nextSenderId = nextMsg.senderId || null;
      isGroupEnd = msgSenderId !== nextSenderId || msgDate !== nextDate;
    }

    let isGroupStart = true;
    if (i > 0) {
      const prevMsg = sorted[i - 1];
      const prevDate = dayjs(prevMsg.sentAt).format('YYYY-MM-DD');
      const prevSenderId = prevMsg.senderId || null;
      isGroupStart = msgSenderId !== prevSenderId || msgDate !== prevDate || showDateMarker;
    }

    hideAvatar = !isGroupEnd;
    hideName = !isGroupStart;

    result.push({ ...msg, hideAvatar, hideName, showDateMarker });
  }

  return result;
}
